import React, { useState, useEffect } from 'react';
import '../Css/AdminCategories.css';
import { API_URL } from '../utils/Function';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faInfoCircle,
  faHeading,
  faLink,
  faAlignLeft,
  faIcons,
  faListUl,
  faExclamationTriangle,
  faSort,
  faSortUp,
  faSortDown,
  faBlog,
  faProjectDiagram,
  faHashtag
} from '@fortawesome/free-solid-svg-icons';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    type: 'both',
    icon: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/category.php/get`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error fetching categories');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditMode(true);
      setSelectedCategory(category);
      setFormData({
        name: category.name || '',
        slug: category.slug || '',
        description: category.description || '',
        type: category.type || 'both',
        icon: category.icon || ''
      });
    } else {
      setEditMode(false);
      setSelectedCategory(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        type: 'both',
        icon: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedCategory(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let url, method;
      const requestData = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        type: formData.type,
        icon: formData.icon
      };

      if (editMode) {
        url = `${API_URL}/category.php/put/${selectedCategory.id}`;
        method = 'PUT';
      } else {
        url = `${API_URL}/category.php/post`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();
      if (data.success) {
        fetchCategories();
        handleCloseModal();
        if (editMode) {
          toast.success('Category updated successfully!', { className: 'toast-edit' });
        } else {
          toast.success('Category added successfully!', { className: 'toast-add' });
        }
      } else {
        toast.error(data.error || 'An error occurred', { className: 'toast-error' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while submitting the form', { className: 'toast-error' });
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/category.php/delete/${deleteId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        fetchCategories();
        setShowDeleteModal(false);
        setDeleteId(null);
        toast.warning('Category deleted successfully!', { className: 'toast-delete' });
      } else {
        toast.error(data.error || 'An error occurred', { className: 'toast-error' });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('An error occurred while deleting the category', { className: 'toast-error' });
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const filteredAndSortedCategories = categories
    .filter(category => 
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

  const getTypeBadge = (type) => {
    const typeConfig = {
      blog: { label: 'Blog', color: 'primary', icon: faBlog },
      project: { label: 'Project', color: 'success', icon: faProjectDiagram },
      both: { label: 'Both', color: 'info', icon: faListUl }
    };
    
    const config = typeConfig[type] || typeConfig.both;
    return (
      <span className={`badge bg-${config.color} type-badge`}>
        <FontAwesomeIcon icon={config.icon} className="me-1" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="admin-categories-container">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="admin-categories-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="admin-categories-heading">
                <FontAwesomeIcon icon={faFolder} className="me-3" />
                Categories Management
              </h1>
            </div>
            <div className="col-md-6 text-end">
              <button className="add-category-btn" onClick={() => handleOpenModal()}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New Category
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-4">
        {/* Search and Stats */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="search-box">
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="stats-card">
              <div className="stat-item">
                <FontAwesomeIcon icon={faHashtag} className="stat-icons" />
                <div className="stat-info">
                  <span className="stat-number">{categories.length}</span>
                  <span className="stat-label">Total Categories</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredAndSortedCategories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FontAwesomeIcon icon={faFolder} />
            </div>
            <p className="empty-state-text">
              {searchTerm ? 'No categories found matching your search.' : 'No categories found. Create your first category!'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover categories-table">
              <thead className="table-header">
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    <span>Name</span>
                    <FontAwesomeIcon icon={getSortIcon('name')} className="sort-icon" />
                  </th>
                  <th onClick={() => handleSort('slug')} className="sortable">
                    <span>Slug</span>
                    <FontAwesomeIcon icon={getSortIcon('slug')} className="sort-icon" />
                  </th>
                  <th>Description</th>
                  <th onClick={() => handleSort('type')} className="sortable">
                    <span>Type</span>
                    <FontAwesomeIcon icon={getSortIcon('type')} className="sort-icon" />
                  </th>
                  <th>Usage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedCategories.map((category) => (
                  <tr key={category.id} className="table-row">
                    <td className="category-name">
                      {category.icon && (
                        <FontAwesomeIcon icon={category.icon} className="me-2 category-icon" />
                      )}
                      {category.name}
                    </td>
                    <td className="category-slug">/{category.slug}</td>
                    <td className="category-description">
                      {category.description || <span className="text-muted">No description</span>}
                    </td>
                    <td>
                      {getTypeBadge(category.type)}
                    </td>
                    <td className="category-usage">
                      <div className="usage-stats">
                        <span className="blog-count">
                          <FontAwesomeIcon icon={faBlog} className="me-1" />
                          {category.blog_count || 0}
                        </span>
                        <span className="project-count">
                          <FontAwesomeIcon icon={faProjectDiagram} className="me-1" />
                          {category.project_count || 0}
                        </span>
                      </div>
                    </td>
                    <td className="action-buttons">
                      <button 
                        className="btn btn-sm btn-outline-primary me-2" 
                        onClick={() => handleOpenModal(category)}
                        title="Edit Category"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-danger" 
                        onClick={() => handleDeleteClick(category.id)}
                        title="Delete Category"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={editMode ? faEdit : faPlus} className="me-2" />
                  {editMode ? 'Edit Category' : 'Add New Category'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit} id="categoryForm">
                  <div className="row">
                    <div className="col-12 mb-4">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Category Information
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faHeading} className="me-1" />
                        Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="name" 
                        value={formData.name} 
                        onChange={handleInputChange} 
                        placeholder="Enter category name"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faLink} className="me-1" />
                        Slug <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="slug" 
                        value={formData.slug} 
                        onChange={handleInputChange} 
                        placeholder="category-url-slug"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faAlignLeft} className="me-1" />
                        Description
                      </label>
                      <textarea 
                        className="form-control" 
                        name="description" 
                        value={formData.description} 
                        onChange={handleInputChange} 
                        rows="3" 
                        placeholder="Category description (optional)"
                      ></textarea>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faListUl} className="me-1" />
                        Type <span className="text-danger">*</span>
                      </label>
                      <select 
                        className="form-select" 
                        name="type" 
                        value={formData.type} 
                        onChange={handleInputChange}
                        required
                      >
                        <option value="both">Both Blogs & Projects</option>
                        <option value="blog">Blog Only</option>
                        <option value="project">Project Only</option>
                      </select>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faIcons} className="me-1" />
                        Icon
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="icon" 
                        value={formData.icon} 
                        onChange={handleInputChange} 
                        placeholder="fa-folder, fa-tag, etc."
                      />
                      <small className="text-muted">
                        FontAwesome icon class (e.g., fa-folder, fa-tag)
                      </small>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="submit" form="categoryForm" className="btn btn-primary">
                  <FontAwesomeIcon icon={editMode ? faSave : faPlus} className="me-1" />
                  {editMode ? 'Update Category' : 'Add Category'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Confirm Delete
                </h5>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this category? This action cannot be undone.</p>
                <p className="text-muted">
                  Note: Categories that are being used by blogs or projects cannot be deleted.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}